import { Badge, Table, type TableColumn } from '../../ui';
import {
  GOAL_LEVELS,
  GOAL_TYPES,
  PLANNING_PERIODS,
  optionLabel,
} from '../../features/goals/goalConfig';
import { statusLabel, statusTone } from '../../features/goals/goalModel';
import type { GoalSummary } from '../../features/goals/types';

export interface GoalListViewProps {
  goals: GoalSummary[];
  loading?: boolean;
  onSelect: (goal: GoalSummary) => void;
}

/** Compact percentage cell (or an em-dash when no progress is recorded). */
function ProgressCell({ value }: { value?: number }) {
  if (value === undefined) return <span className="text-zinc-600">—</span>;
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-surface-3">
        <div
          className="h-full rounded-full bg-accent"
          style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
        />
      </div>
      <span className="text-xs tabular-nums text-zinc-400">{value}%</span>
    </div>
  );
}

/** Tabular list of goals with status, taxonomy and traceability hints. */
export function GoalListView({ goals, loading, onSelect }: GoalListViewProps) {
  const columns: TableColumn<GoalSummary>[] = [
    {
      key: 'title',
      header: 'Goal',
      render: (g) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-zinc-100">{g.title}</p>
          <p className="truncate text-xs text-zinc-500">
            {optionLabel(GOAL_LEVELS, g.level)}
            {g.owningTeam ? ` · ${g.owningTeam}` : ''}
          </p>
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      render: (g) => (
        <span className="text-zinc-300">{optionLabel(GOAL_TYPES, g.goalType)}</span>
      ),
    },
    {
      key: 'period',
      header: 'Period',
      render: (g) => (
        <span className="text-zinc-300">
          {optionLabel(PLANNING_PERIODS, g.planningPeriod)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (g) => (
        <Badge tone={statusTone(g.status)} dot>
          {statusLabel(g.status)}
        </Badge>
      ),
    },
    {
      key: 'progress',
      header: 'Progress',
      render: (g) => <ProgressCell value={g.manualProgress} />,
    },
    {
      key: 'linked',
      header: 'Linked work',
      align: 'right',
      render: (g) => (
        <span className="text-xs tabular-nums text-zinc-400">
          {g.linkedStoryCount} {g.linkedStoryCount === 1 ? 'story' : 'stories'}
        </span>
      ),
    },
    {
      key: 'lead',
      header: 'Lead',
      render: (g) => (
        <span className="text-zinc-300">{g.goalLead || '—'}</span>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      rows={goals}
      rowKey={(g) => g.id}
      loading={loading}
      onRowClick={onSelect}
      empty="No goals match the current filters."
    />
  );
}

export default GoalListView;
